// @flow

import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/styles';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import {Hidden, Typography, IconButton} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import CustomEditor from '../CustomEditor/CustomEditor';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useIsFeatureInTier} from '../../../../../utils/component';
import type {FaqQuestion} from '../../duck';
type Props = {
  idx: number,
  question: FaqQuestion,
  currentQuestion: number,
  handleUpdateQuestion: (qs: FaqQuestion, id: number) => void,
  handleCurrentQuestion: (index: number) => void,
  saveStoreFaqConfig: ({legalConfigRequest: {[key: string]: any}}) => void,
  editStoreFaqConfig: ({
    legalConfigRequest: {[key: string]: any},
    id: string,
  }) => void,
  handleDeleteFaq: (id: string | number, question?: FaqQuestion) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    gap: '8px',
    justifyContent: 'flex-start',
  },
  containerEditor: {
    width: '100%',
    borderRadius: '8px',
    position: 'relative',
    padding: theme.spacing(2),
    backgroundColor: colors.grey200,
  },
  numberContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: colors.grey300,
    width: '2rem',
    height: '2rem',
    '& p': {
      margin: 0,
      color: colors.grey800,
      fontSize: 16,
      fontWeight: 700,
    },
    [theme.breakpoints.up('md')]: {
      width: '1.5rem',
      height: '1.5rem',
    },
  },
  hiddenBlock: {
    display: 'flex',
    flexDirection: 'row-reverse',
    height: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 0,
    },
  },
  singleEditor: {
    position: 'static',
    margin: theme.spacing(1, 0),
  },
  wrapSave: {
    margin: theme.spacing(2, 0),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  buttonSave: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #D4D4DD',
    padding: theme.spacing(1, 3),
    [theme.breakpoints.up('md')]: {
      backgroundColor: '#FFFFFF',
    },
  },
  btnDelete: {
    padding: 0,
    margin: theme.spacing(0, 1),
  },
  deleteIcon: {
    color: '#E13065',
  },
}));

export default function SingleQuestion(props: Props) {
  const {
    idx,
    question,
    handleUpdateQuestion,
    handleCurrentQuestion,
    currentQuestion,
    saveStoreFaqConfig,
    editStoreFaqConfig,
    handleDeleteFaq,
  } = props;

  const classes = useStyles();

  const [inputFocused, setInputFocused] = useState(0);

  const hasFaqInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const handleQuestionChange = useCallback(
    (text: string) => {
      handleUpdateQuestion({...question, question: text}, idx);
    },
    [handleUpdateQuestion, idx, question]
  );

  const handleAnswerChange = useCallback(
    (text: string) => {
      handleUpdateQuestion({...question, answer: text}, idx);
    },
    [handleUpdateQuestion, idx, question]
  );

  const handleInputFocused = useCallback(
    (i: number) => {
      handleCurrentQuestion(idx);
      setInputFocused(i);
    },
    [handleCurrentQuestion, idx]
  );

  const handleSaveFaq = () => {
    if (!hasFaqInformationFeature) return;
    if (question && question.id) {
      editStoreFaqConfig({
        legalConfigRequest: {
          enabled: true,
          question: question ? question.question : '',
          answer: question ? question.answer : '',
        },
        id: question.id,
      });
    } else {
      saveStoreFaqConfig({
        legalConfigRequest: {
          enabled: true,
          question: question ? question.question : '',
          answer: question ? question.answer : '',
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    if (!hasFaqInformationFeature) return;
    if (question && question.id) {
      handleDeleteFaq(question.id, question);
    } else {
      handleDeleteFaq(idx, undefined);
    }
  };

  return (
    <div data-color-mode="light">
      <Hidden mdUp>
        <div className={classes.wrapSave}>
          <div className={classes.numberContainer}>
            <Typography>{idx + 1}</Typography>
          </div>
          <div>
            <CustomButton
              disabled={!hasFaqInformationFeature}
              className={classes.buttonSave}
              onClick={handleSaveFaq}
            >
              Guardar
            </CustomButton>
            <IconButton onClick={handleDelete}>
              <DeleteOutlineIcon className={classes.deleteIcon} />
            </IconButton>
          </div>
        </div>
      </Hidden>
      <div className={classes.root}>
        <Hidden smDown>
          <div className={classes.numberContainer}>
            <Typography>{idx + 1}</Typography>
          </div>
        </Hidden>
        <div className={classes.containerEditor}>
          <div className={classes.hiddenBlock}>
            <Hidden smDown>
              <CustomButton
                disabled={!hasFaqInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveFaq}
              >
                Guardar
              </CustomButton>
              <IconButton onClick={handleDelete} className={classes.btnDelete}>
                <DeleteOutlineIcon className={classes.deleteIcon} />
              </IconButton>
            </Hidden>
          </div>
          <div className={classes.singleEditor}>
            <Hidden mdUp>
              <CustomEditor
                disabled={!hasFaqInformationFeature}
                selected={currentQuestion === idx && inputFocused === 0}
                height={56}
                idx={0}
                text={question ? question.question : ''}
                handleInputFocused={handleInputFocused}
                onChange={handleQuestionChange}
                placeholder="Pregunta"
              />
            </Hidden>
            <Hidden smDown>
              <CustomEditor
                disabled={!hasFaqInformationFeature}
                selected={inputFocused === 0}
                height={56}
                idx={0}
                text={question ? question.question : ''}
                handleInputFocused={handleInputFocused}
                onChange={handleQuestionChange}
                placeholder="Pregunta"
              />
            </Hidden>
          </div>
          <div className={classes.singleEditor}>
            <Hidden mdUp>
              <CustomEditor
                disabled={!hasFaqInformationFeature}
                selected={currentQuestion === idx && inputFocused === 1}
                idx={1}
                height={112}
                text={question ? question.answer : ''}
                handleInputFocused={handleInputFocused}
                onChange={handleAnswerChange}
                placeholder="Escribe aquí la respuesta"
              />
            </Hidden>
            <Hidden smDown>
              <CustomEditor
                disabled={!hasFaqInformationFeature}
                selected={inputFocused === 1}
                idx={1}
                height={112}
                text={question ? question.answer : ''}
                handleInputFocused={handleInputFocused}
                onChange={handleAnswerChange}
                placeholder="Escribe aquí la respuesta"
              />
            </Hidden>
          </div>
        </div>
      </div>
    </div>
  );
}

SingleQuestion.propTypes = {
  idx: PropTypes.number.isRequired,
  question: PropTypes.object.isRequired,
  currentQuestion: PropTypes.number.isRequired,
  handleUpdateQuestion: PropTypes.func.isRequired,
  handleCurrentQuestion: PropTypes.func.isRequired,
  saveStoreFaqConfig: PropTypes.func.isRequired,
  editStoreFaqConfig: PropTypes.func.isRequired,
  handleDeleteFaq: PropTypes.func.isRequired,
};
